/* Dependencies */
import anime from 'animejs';

// Utils
import { TrapFocus } from '../../Utils/TrapFocus/TrapFocus';

// Models
import { MegaMenuSearchProps } from './MegaMenuSearch.model';

/**
 * Mega Menu Search
 */
class MegaMenuSearch {
  isOpen = false;
  trapFocus: TrapFocus;

  triggerElements: HTMLElement[];
  searchElement: HTMLElement;
  closeElement: HTMLElement;

  constructor(props: MegaMenuSearchProps) {
    this.triggerElements = props.triggerElements;
    this.searchElement = props.searchElement;
    this.closeElement = props.closeElement;

    // Create trap focus instance
    this.trapFocus = new TrapFocus(this.searchElement, {
      escapeClose: true,
      escapeCloseCB: () => {
        this.closeDrawer();
      },
    });

    // Attach events
    this.attachEvents();
  }

  /**
   * Attaches the relevant event listeners.
   */
  attachEvents(): void {
    // Open search triggers
    this.triggerElements.forEach((element) => {
      element.addEventListener('click', () => {
        this.isOpen ? this.closeDrawer() : this.openDrawer(true);
      });
    });

    // Close search trigger
    this.closeElement.addEventListener('click', () => {
      this.closeDrawer();
    });

    // Global close event.
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-mega-menu-search]') && this.isOpen) {
        this.closeDrawer();
        return;
      }
    });
  }

  /**
   * Sets the ariua lables for the drawer.
   * @param status - Status to be set.
   */
  setDrawerExpanded(status: boolean) {
    this.triggerElements.forEach((element) => {
      element.setAttribute('aria-expanded', status.toString());
    });
  }

  /**
   * Opens the search drawer.
   * @param handleTrapFocus - Whether to handle trap focus.
   */
  openDrawer(handleTrapFocus: boolean): void {
    // If already open, return.
    if (this.isOpen) return;

    // Animate the drawer open.
    anime({
      begin: () => {
        this.searchElement.classList.remove('tw-hidden');
      },
      targets: this.searchElement,
      translateY: ['-100%', '0'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        // Update state
        this.isOpen = true;
        this.setDrawerExpanded(true);

        // Lock user focus.
        if (handleTrapFocus) {
          this.trapFocus.attachTrapFocus();
        }
      },
    });
  }

  /**
   * Closes the search drawer.
   */
  closeDrawer() {
    // If already closed, return.
    if (!this.isOpen) return;

    // Update State
    this.isOpen = false;
    this.setDrawerExpanded(false);

    //  Animate the drawer closed.
    anime({
      targets: this.searchElement,
      translateY: [0, '-100%'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        // Unlock user focus.
        if (this.trapFocus.activeState) {
          this.trapFocus.dettachTrapFocus();
        }

        // Hide the drawer.
        this.searchElement.classList.add('tw-hidden');
      },
    });
  }
}

/**
 * Meag Manu Search Init
 */
export const megaMenuSearchInit = (): void => {
  // Look up required elements.
  const searchElement: HTMLElement = document.querySelector(
    '[data-mega-menu-search]'
  );
  const closeElement: HTMLElement = document.querySelector(
    '[data-mega-menu-search-close]'
  );
  const triggerElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-mega-menu-search-trigger]')
  );

  // If no elements found, return.
  if (!searchElement) return;

  // Create instance of mega menu search.
  new MegaMenuSearch({
    triggerElements,
    searchElement,
    closeElement,
  });
};
